import { createStore } from "zustand/vanilla";
import { conflict, err } from "@pack-it-up/shared";
import { planParticipantRemoval, previewParticipantRemoval } from "@pack-it-up/core";
import { errorToMessage } from "../errorToMessage.js";
// Doc 08 §8 — past this many enumerated members the dialog nudges toward a link.
export const MAX_ENUMERATED_MEMBERS = 25;
const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
// Doc 08 §3: the share field does double duty. Exactly one bucket per input —
// friend matches win, then a valid non-friend email, then a guarded managed
// profile, else invalid.
export const classifyShareInput = (text, friends, managed) => {
    const q = text.trim().toLowerCase();
    if (q === "")
        return { kind: "invalid" };
    const friendMatches = friends.filter((f) => f.displayName.toLowerCase().includes(q) || f.email.toLowerCase().includes(q));
    if (friendMatches.length > 0)
        return { kind: "friend", matches: friendMatches };
    if (isValidEmail(text))
        return { kind: "email", email: text.trim() };
    const managedMatches = managed.filter((m) => m.displayName.toLowerCase().includes(q));
    if (managedMatches.length > 0)
        return { kind: "managed", matches: managedMatches };
    return { kind: "invalid" };
};
export const createShareStore = (deps) => {
    // Stream subscriptions are owned by the store: dispose() unsubscribes them all, so a
    // superseded store stops writing into state nothing renders. `subs` is filled after
    // the store exists (the streams push into it).
    const subs = [];
    const store = createStore((set) => {
        // Enumerated membership is capped: once a trip is at MAX_ENUMERATED_MEMBERS,
        // adding another enumerated member is blocked — the dialog must switch to an
        // invite link (Doc 08 §8). Returns the blocking Err when capped, else null.
        const blockIfCapped = () => {
            if (deps.memberCount() >= MAX_ENUMERATED_MEMBERS) {
                const e = conflict("This trip is at the member limit — share with an invite link instead.");
                set({ capPrompt: true, error: errorToMessage(e) });
                return err(e);
            }
            return null;
        };
        const reportErr = (r) => {
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        };
        return {
            pendingInvites: [],
            links: [],
            capPrompt: false,
            error: null,
            classify: (text) => classifyShareInput(text, deps.friends(), deps.managed()),
            async shareWithUser(userId, role) {
                const blocked = blockIfCapped();
                if (blocked)
                    return blocked;
                return reportErr(await deps.sharing.shareWithUser(deps.resource, userId, role));
            },
            async inviteByEmail(email, role) {
                return reportErr(await deps.sharing.inviteByEmail(deps.resource, email, role));
            },
            async revokePendingInvite(inviteId) {
                return reportErr(await deps.sharing.revokePendingInvite(inviteId));
            },
            async addManagedMember(profileId) {
                const blocked = blockIfCapped();
                if (blocked)
                    return blocked;
                return reportErr(await deps.sharing.addManagedMember(deps.resource, profileId));
            },
            async changeRole(userId, role) {
                return reportErr(await deps.sharing.changeRole(deps.resource, userId, role));
            },
            previewRemoveMember: (participantId) => previewParticipantRemoval(participantId, deps.rows?.() ?? []),
            async removeMember(userId) {
                // Rows first, while the member still exists: a row that loses its last
                // allocation is visibly theirs until then. No-op when no row named them.
                const apply = deps.applyParticipantRemovalPlan;
                if (apply !== undefined) {
                    const plan = planParticipantRemoval(userId, deps.rows?.() ?? []);
                    const cleanup = await apply(plan);
                    if (!cleanup.ok)
                        return reportErr(cleanup);
                }
                return reportErr(await deps.sharing.removeMember(deps.resource, userId));
            },
            async renameGuest(userId, displayName) {
                return reportErr(await deps.sharing.renameGuest(deps.resource, userId, displayName));
            },
            async createLink(role, opts) {
                return reportErr(await deps.sharing.createInviteLink(deps.resource, role, opts));
            },
            async revokeLink(token) {
                return reportErr(await deps.sharing.revokeInviteLink(token));
            },
            dispose() {
                for (const unsubscribe of subs.splice(0))
                    unsubscribe();
            },
        };
    });
    if (deps.canManage) {
        subs.push(deps.sharing.observeMyPendingInvites(deps.resource).subscribe((pendingInvites) => store.setState({ pendingInvites })), deps.sharing.observeInviteLinks(deps.resource).subscribe((links) => store.setState({ links })));
    }
    return store;
};
//# sourceMappingURL=shareStore.js.map